/**
 * BlockTypeBadge — etiqueta visual del tipo de bloque para el árbol
 * estructural y para el header del editor.
 *
 * Estilos por categoría siguiendo Doc 18 §2.4 (tokens semánticos):
 *   - rich_text / plain_text       → ink-muted (texto)
 *   - bullet_list / numbered_list / checklist → review (lista)
 *   - key_value_grid / structured  → brand (estructurado)
 *   - table / structured_table     → official-soft (datos)
 *   - diagram / mermaid_*          → official-soft (visual)
 *   - fixed_heading / fixed_block  → official (estructural fuerte)
 *   - code_block                   → mono (técnico)
 *   - image_upload / file_upload / embed_canon → ink-subtle (external/v2)
 *
 * Tamaño compacto por default (xs); para el header del editor usar
 * size="sm".
 */

import type { BlockType } from '../../../api/templates'
import { getBlockMeta } from './blockTypeCatalog'

interface BlockTypeBadgeProps {
  type: BlockType
  size?: 'xs' | 'sm'
}

export function BlockTypeBadge({ type, size = 'xs' }: BlockTypeBadgeProps) {
  const meta = getBlockMeta(type)
  const label = meta?.shortLabel ?? type
  const colorClass = COLOR_CLASS[type] ?? 'bg-surface-muted text-ink-muted'
  const sizeClass =
    size === 'sm'
      ? 'px-2 py-0.5 text-[10px]'
      : 'px-1.5 py-0 text-[9px]'
  return (
    <span
      className={`inline-flex items-center rounded font-mono uppercase tracking-wide ${sizeClass} ${colorClass}`}
      title={meta?.description}
    >
      {label}
    </span>
  )
}

const COLOR_CLASS: Partial<Record<BlockType, string>> = {
  // Texto
  rich_text: 'bg-surface-muted text-ink-muted',
  plain_text: 'bg-surface-muted text-ink-muted',
  key_value_grid: 'bg-brand-soft text-brand',
  // Listas
  bullet_list: 'bg-review-soft text-review',
  numbered_list: 'bg-review-soft text-review',
  checklist: 'bg-review-soft text-review',
  // Tabla
  structured_table: 'bg-official-soft text-official',
  // Diagramas
  diagram: 'bg-official-soft text-official',
  mermaid_flowchart: 'bg-official-soft text-official',
  mermaid_sequence: 'bg-official-soft text-official',
  mermaid_use_case: 'bg-official-soft text-official',
  mermaid_activity: 'bg-official-soft text-official',
  mermaid_state: 'bg-official-soft text-official',
  mermaid_er: 'bg-official-soft text-official',
  mermaid_class: 'bg-official-soft text-official',
  mermaid_gantt: 'bg-official-soft text-official',
  mermaid_journey: 'bg-official-soft text-official',
  mermaid_mindmap: 'bg-official-soft text-official',
  mermaid_generic: 'bg-official-soft text-official',
  // Otros
  structured: 'bg-brand-soft text-brand',
  code_block: 'bg-surface-muted text-ink-subtle',
  // Contenido fijo
  fixed_heading: 'bg-official-soft text-official',
  fixed_block: 'bg-official-soft text-official',
  // Pendientes (B.6.d.3.c)
  image_upload: 'bg-surface-muted text-ink-subtle',
  file_upload: 'bg-surface-muted text-ink-subtle',
  embed_canon: 'bg-surface-muted text-ink-subtle',
}
